import { useState } from 'react';
import { capitalizeFirst } from '../../shared/sentence';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  COST_CURRENCIES,
  COST_FIELDS,
  FEATURE_COST_RATES_HISTORY,
  costCurrency,
  costValue,
  missingCount,
  saveCostParameters,
  scheduledCostRates,
  costRateValueText,
  unitLabel,
  type CostCurrency,
  type CostKey,
  type CurrencyKey,
} from './costParametersApi';
import { useFeature } from '../featureFlags/FeatureGate';
import { fromDateTimeLocalValue } from '../../shared/clientTime';
import { fmtDate } from '../../shared/Panel';
import { CostRateHistoryDialog } from './CostRateHistory';

/**
 * Parametry kosztu dolotu spółki (I.02).
 *
 * Komórka mówi wprost, czy koszt w podpowiedziach da się policzyć: przy
 * brakach pokazuje ile, bo „brak 3 z 5” tłumaczy, czemu przy kandydatach
 * widać „koszt nieznany”. Stawki wpisuje osoba z sekcji administracyjnej —
 * inni dostaną odmowę z API, a przycisk jej nie ukrywa (ukryty przycisk
 * nie jest uprawnieniem).
 */
export const CostParameters = ({
  record,
  onSaved,
}: {
  record: Record<string, unknown>;
  onSaved?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const missing = missingCount(record);
  // Historia, powód, data i właściciel stawek (24.10.2026, S-24.10-12).
  const historyOn = useFeature(FEATURE_COST_RATES_HISTORY);
  const scheduled = historyOn ? scheduledCostRates(record) : [];

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      // Z historią w komórce są dwa przyciski i chip — zawijają się, żeby
      // lista spółek mieściła się przy 1366 px (S-24.10-12).
      sx={historyOn ? { alignItems: 'center', flexWrap: 'wrap', maxWidth: 220 } : { alignItems: 'center' }}
    >
      {missing === 0 ? (
        <Chip size="small" color="success" variant="outlined" label="komplet" />
      ) : (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`brak ${missing} z ${COST_FIELDS.length}`}
        />
      )}
      {scheduled.length > 0 && (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          data-testid={`cost-rates-scheduled-${String(record.id)}`}
          label={`zaplanowane: ${scheduled.length}`}
          title={scheduled
            .map((c) => `${c.label}: ${costRateValueText(c.newValue, c.newText)} od ${fmtDate(c.validFrom)}`)
            .join('; ')}
        />
      )}
      <Button size="small" onClick={() => setOpen(true)}>
        Ustaw
      </Button>
      {historyOn && (
        <Button
          size="small"
          onClick={() => setHistoryOpen(true)}
          data-testid={`cost-rates-history-${String(record.id)}`}
        >
          Historia
        </Button>
      )}
      {open && (
        <CostParametersDialog
          record={record}
          withHistory={historyOn}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved?.();
          }}
        />
      )}
      {historyOpen && (
        <CostRateHistoryDialog
          record={record}
          onClose={() => setHistoryOpen(false)}
          onChanged={onSaved}
        />
      )}
    </Stack>
  );
};

function CostParametersDialog({
  record,
  onClose,
  onSaved,
  withHistory = false,
}: {
  record: Record<string, unknown>;
  onClose: () => void;
  onSaved: () => void;
  /** Powód, „obowiązuje od” i właściciel (S-24.10-12). */
  withHistory?: boolean;
}) {
  const [reason, setReason] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [owner, setOwner] = useState(
    typeof record.costRatesOwner === 'string' ? record.costRatesOwner : '',
  );
  const [values, setValues] = useState<Record<CostKey, string>>(() => {
    const start = {} as Record<CostKey, string>;
    COST_FIELDS.forEach((f) => {
      const v = costValue(record, f.key);
      start[f.key] = v === null ? '' : String(v);
    });
    return start;
  });
  const [currencies, setCurrencies] = useState<Record<CurrencyKey, CostCurrency>>(() => {
    const start = {} as Record<CurrencyKey, CostCurrency>;
    COST_FIELDS.forEach((f) => {
      if (f.currencyKey) start[f.currencyKey] = costCurrency(record, f.currencyKey);
    });
    return start;
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const payload: Partial<Record<CostKey, number | null> & Record<CurrencyKey, CostCurrency>> = {
      ...currencies,
    };
    for (const f of COST_FIELDS) {
      const raw = values[f.key].trim().replace(',', '.');
      if (raw === '') {
        payload[f.key] = null;
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) {
        setError(`${f.label}: wpisz liczbę większą od zera albo zostaw puste`);
        return;
      }
      payload[f.key] = n;
    }
    if (withHistory && reason.trim() === '') {
      setError('Podaj powód zmiany stawek');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveCostParameters(
        record.id as number,
        payload,
        withHistory
          ? {
              reason: reason.trim(),
              validFrom: fromDateTimeLocalValue(validFrom),
              owner: owner.trim() === '' ? null : owner.trim(),
            }
          : undefined,
      );
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ? capitalizeFirst(msg) : 'Nie udało się zapisać parametrów');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Parametry kosztu dolotu — {String(record.name ?? '')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Stawki podaje Marathon. Puste pole oznacza, że parametr nie jest znany — wtedy koszt
          dolotu przy kandydatach też jest nieznany, a nie zerowy. Stawkę w EUR przeliczamy na złote
          kursem średnim NBP (tabela A) z dnia realizacji zlecenia.
        </Typography>
        <Stack spacing={2}>
          {COST_FIELDS.map((f) => {
            const currencyKey = f.currencyKey;
            const currency = currencyKey ? currencies[currencyKey] : null;
            return (
              <Stack key={f.key} direction="row" spacing={1}>
                <TextField
                  label={`${f.label} (${unitLabel(f.unit, currency)})`}
                  value={values[f.key]}
                  onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                  size="small"
                  sx={{ flex: 1 }}
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                />
                {currencyKey && (
                  <TextField
                    select
                    label="Waluta"
                    value={currency}
                    onChange={(e) =>
                      setCurrencies((s) => ({ ...s, [currencyKey]: e.target.value as CostCurrency }))
                    }
                    size="small"
                    sx={{ width: 96 }}
                  >
                    {COST_CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            );
          })}
          {withHistory && (
            <>
              <TextField
                label="Właściciel stawek"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                size="small"
                helperText="Puste pole — właściciel nieustalony."
                slotProps={{ htmlInput: { maxLength: 255, 'data-testid': 'cost-rates-owner' } }}
              />
              <TextField
                type="datetime-local"
                label="Obowiązuje od (czas polski)"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                size="small"
                helperText="Puste — zmiana stawek działa od razu. Data w przyszłości — do tego czasu koszt liczy obecna stawka."
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'data-testid': 'cost-rates-valid-from' },
                }}
              />
              <TextField
                label="Powód zmiany"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                multiline
                minRows={2}
                required
                slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'cost-rates-reason' } }}
              />
            </>
          )}
        </Stack>
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" disabled={busy}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
